import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, catchError, map, of } from 'rxjs';
import { DelegationsService } from 'src/app/delegations/delegations.service';
import * as DelegationsActions from 'src/app/delegations/store/actions/delegations.actions';
import { HttpResponseHelpers } from 'src/app/shared/helpers';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';

@Injectable()
export class DelegationsEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _service: DelegationsService
    ) {}

    createDelegation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DelegationsActions.createDelegation),
            switchMap((action) => {
                return this._service.createDelegation(action.delegation).pipe(
                    map(() =>
                        DelegationsActions.createDelegationSuccess({
                            ownerId: action.delegation.ownerAspNetUserId,
                            message: 'Coverage set up successfully'
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            DelegationsActions.createDelegationFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    updateDelegation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DelegationsActions.updateDelegation),
            switchMap((action) => {
                return this._service.updateDelegation(action.delegation).pipe(
                    map(() =>
                        DelegationsActions.updateDelegationSuccess({
                            ownerId: action.delegation.ownerAspNetUserId,
                            message: 'Your changes have been saved.'
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            DelegationsActions.updateDelegationFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    searchDelegationEmployees$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DelegationsActions.searchDelegationEmployees),
            switchMap((action) => {
                return this._service.searchDelegationEmployees(action.query).pipe(
                    map((delegationEmployees) =>
                        DelegationsActions.searchDelegationEmployeesSuccess({ delegationEmployees })
                    ),
                    catchError((error: unknown) =>
                        of(
                            DelegationsActions.searchDelegationEmployeesFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    getAssignedToMeDelegations$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DelegationsActions.getAssignedToMeDelegations),
            switchMap((action) => {
                return this._service.getDelegateeDelegations(action.delegateeId).pipe(
                    map((delegations) => DelegationsActions.getAssignedToMeDelegationsSuccess({ delegations })),
                    catchError((error: unknown) =>
                        of(
                            DelegationsActions.getAssignedToMeDelegationsFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    getDelegationTypes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DelegationsActions.getDelegationTypes),
            switchMap(() => {
                return this._service.getDelegationTypes().pipe(
                    map((delegationTypes) =>
                        DelegationsActions.getDelegationTypesSuccess({
                            delegationTypes: delegationTypes
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            DelegationsActions.getDelegationTypesFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    getOwnerDelegations$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                DelegationsActions.getOwnerDelegations,
                DelegationsActions.createDelegationSuccess,
                DelegationsActions.updateDelegationSuccess,
                DelegationsActions.setDelegationToInactiveSuccess
            ),
            switchMap((action) => {
                return this._service.getOwnerDelegations(action.ownerId).pipe(
                    map((delegations) => DelegationsActions.getOwnerDelegationsSuccess({ delegations })),
                    catchError((error: unknown) =>
                        of(
                            DelegationsActions.getOwnerDelegationsFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    setDelegationtoInactive$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(DelegationsActions.setDelegationToInactive),
            switchMap((action) => {
                return this._service.setDelegationToInactive(action.delegation.id).pipe(
                    map(() =>
                        DelegationsActions.setDelegationToInactiveSuccess({
                            ownerId: action.delegation.ownerAspNetUserId,
                            message: action.delegation.isCurrent
                                ? 'Successfully ended active coverage.'
                                : 'Successfully cancelled upcoming coverage.'
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            DelegationsActions.setDelegationToInactiveFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    delegationsSuccessToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                DelegationsActions.createDelegationSuccess,
                DelegationsActions.updateDelegationSuccess,
                DelegationsActions.setDelegationToInactiveSuccess
            ),
            map((action) => showToasterMessage({ message: action.message }))
        );
    });

    delegationsFailureToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                DelegationsActions.createDelegationFailure,
                DelegationsActions.updateDelegationFailure,
                DelegationsActions.getAssignedToMeDelegationsFailure,
                DelegationsActions.getOwnerDelegationsFailure,
                DelegationsActions.setDelegationToInactiveFailure
            ),
            map((action) => showToasterMessage({ message: action.error }))
        );
    });
}
